import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { AppShell } from '@/components/layout/AppShell';
import { AgentRunPanel } from '@/components/agents/AgentRunPanel';
import { useToast } from '@/components/ui';
import { agentsApi } from '@/lib/agentsApi';

export function AgentsPage() {
  const { wsId = '' } = useParams<{ wsId: string }>();
  const { t } = useTranslation();
  const [tab, setTab] = useState<'prospect' | 'qa' | 'summary'>('prospect');

  return (
    <AppShell titleKey="agents.title">
      <p className="text-sm text-ink-600 max-w-2xl mb-6">{t('agents.intro')}</p>

      <div className="flex border-b border-ink-200 mb-6 gap-1">
        <TabButton active={tab === 'prospect'} onClick={() => setTab('prospect')}>
          {t('agents.tab_prospect')}
        </TabButton>
        <TabButton active={tab === 'qa'} onClick={() => setTab('qa')}>
          {t('agents.tab_qa')}
        </TabButton>
        <TabButton active={tab === 'summary'} onClick={() => setTab('summary')}>
          {t('agents.tab_summary')}
        </TabButton>
      </div>

      {tab === 'prospect' && <ProspectPanel wsId={wsId} />}
      {tab === 'qa' && <QaPanel wsId={wsId} />}
      {tab === 'summary' && <SummaryPanel wsId={wsId} />}
    </AppShell>
  );
}

function TabButton({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      onClick={onClick}
      className={
        'px-4 py-2 text-sm font-medium border-b-2 -mb-px transition ' +
        (active
          ? 'border-accent-500 text-accent-700'
          : 'border-transparent text-ink-600 hover:text-ink-900')
      }
    >
      {children}
    </button>
  );
}

function ProspectPanel({ wsId }: { wsId: string }) {
  const { t } = useTranslation();
  const toast = useToast();
  const [prompt, setPrompt] = useState(t('agents.prospect_example'));
  const run = useMutation({
    mutationFn: () => agentsApi.prospect(wsId, prompt),
    onError: () => toast.error(t('agents.run_failed')),
  });

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      <div>
        <label className="text-xs uppercase tracking-[0.16em] text-ink-500 font-semibold">
          {t('agents.prompt_label')}
        </label>
        <textarea
          value={prompt}
          onChange={(e) => setPrompt(e.target.value)}
          rows={6}
          className="mt-2 w-full rounded-md border border-ink-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-accent-500/30 font-mono"
        />
        <button
          onClick={() => run.mutate()}
          disabled={run.isPending}
          className="mt-3 inline-flex items-center px-5 py-2.5 rounded-md bg-ink-900 text-white text-sm font-semibold hover:bg-ink-800 transition disabled:opacity-60"
        >
          {run.isPending ? t('agents.running') : t('agents.run_button')}
        </button>
      </div>
      <AgentRunPanel response={run.data} prompt={prompt} isPending={run.isPending} />
    </div>
  );
}

function QaPanel({ wsId }: { wsId: string }) {
  const { t } = useTranslation();
  const toast = useToast();
  const [prompt, setPrompt] = useState(t('agents.qa_example'));
  const run = useMutation({
    mutationFn: () => agentsApi.salesQa(wsId, prompt),
    onError: () => toast.error(t('agents.run_failed')),
  });
  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      <div>
        <label className="text-xs uppercase tracking-[0.16em] text-ink-500 font-semibold">
          {t('agents.ask_label')}
        </label>
        <textarea
          value={prompt}
          onChange={(e) => setPrompt(e.target.value)}
          rows={6}
          className="mt-2 w-full rounded-md border border-ink-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-accent-500/30 font-mono"
        />
        <button
          onClick={() => run.mutate()}
          disabled={run.isPending}
          className="mt-3 inline-flex items-center px-5 py-2.5 rounded-md bg-ink-900 text-white text-sm font-semibold hover:bg-ink-800 transition disabled:opacity-60"
        >
          {run.isPending ? t('common.thinking') : t('agents.ask_button')}
        </button>
      </div>
      <AgentRunPanel
        response={run.data}
        prompt={prompt}
        isPending={run.isPending}
        placeholderKey="agents.reply_placeholder"
      />
    </div>
  );
}

function SummaryPanel({ wsId }: { wsId: string }) {
  const { t, i18n } = useTranslation();
  const toast = useToast();
  const lang = (i18n.language || 'en').split('-')[0];
  const run = useMutation({
    mutationFn: () =>
      agentsApi.signalSummary({
        workspaceId: wsId,
        sinceDays: 7,
        // Summary is written in the user's own UI language (all 14 locales).
        lang,
      }),
    onError: () => toast.error(t('agents.run_failed')),
  });
  return (
    <div className="space-y-6">
      <button
        onClick={() => run.mutate()}
        disabled={run.isPending}
        className="inline-flex items-center px-5 py-2.5 rounded-md bg-ink-900 text-white text-sm font-semibold hover:bg-ink-800 transition disabled:opacity-60"
      >
        {run.isPending ? t('common.generating') : t('agents.summary_button')}
      </button>
      <AgentRunPanel response={run.data} isPending={run.isPending} />
    </div>
  );
}
